import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon } from '@mui/icons-material';
import ProcessFlowDiagramItem from './ProcessFlowDiagramItem';
import useNotification from '../../hooks/useNotification';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import LoadingBox from '../atoms/LoadingBox';
import EmptyStateBox from '../atoms/EmptyStateBox';
import TopBarButtons from '../atoms/TopBarButtons';
import SectionHeader from '../atoms/Header';
import ConfirmationDialog from '../common/ConfirmationDialog';
import ExplanationModal from '../atoms/ExplanationModal';
import ImprovementModal from '../atoms/ImprovementModal';
import VersionSelector from '../atoms/VersionSelector';
import AddDiagramModal from '../atoms/AddDiagramModal';
import useContentActions from '../../hooks/useContentActions';
import useFullScreen from '../../hooks/useFullScreen';
import useExplain from '../../hooks/useExplain';
import useImprove from '../../hooks/useImprove';
import useProcessFlowVersions from '../../hooks/useProcessFlowVersions';
import useDiagramActions from '../../hooks/useDiagramActions';
import conversationService from '../../services/conversationService';
import useParsedSections from '../../hooks/useParsedSections';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';

// Import Mermaid utilities
import mermaid from 'mermaid';
import MermaidEditor from '../mermaid/MermaidEditor';
import { renderMermaidDiagrams, resetDiagramProcessing } from '../../utils/mermaidRenderer';
import { fixMermaidSyntax } from '../../utils/mermaidSyntaxFixer';

/**
 * A split-view layout component that shows conversation on the left
 * and Mermaid diagrams in accordion format on the right
 */
const SplitViewProcessFlows = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const [expandedDiagrams, setExpandedDiagrams] = useState({});
  const [expandedHeights, setExpandedHeights] = useState({});
  const DEFAULT_ZOOM = 0.5;
  const [zoomLevels, setZoomLevels] = useState({});
  const [popupDiagram, setPopupDiagram] = useState(null);
  const [popupZoomLevel, setPopupZoomLevel] = useState(1.0);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, diagram: null });
  const [showAddDiagramModal, setShowAddDiagramModal] = useState(false);
  const { showNotification } = useNotification();

  const diagramContentRef = useRef('');
  const diagramPanelRef = useRef(null);
  const isLocalUpdateRef = useRef(false);
  const prevIsStreamingRef = useRef(isStreaming);

  // Use full screen hook
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // PRIMARY: Fetch from /parsed-responses/latest (backend source of truth)
  const {
    sections: backendSections,
    isLoading: isSectionsLoading,
    contentRef: backendContentRef,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // FALLBACK: Use process flow versions hook for mermaid extraction from messages
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useProcessFlowVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // State for managing current version's diagrams
  const [parsedDiagrams, setParsedDiagrams] = useState([]);

  // Convert backend sections to diagram format (extract mermaid code from content)
  const sectionsToDiagrams = useCallback((sections) => {
    return sections.map((section, index) => {
      const content = section.content || section.rawContent || '';
      // Extract mermaid code block from section content
      const mermaidMatch = content.match(/```mermaid\n([\s\S]*?)```/);
      const code = mermaidMatch ? mermaidMatch[1].trim() : '';
      // Extract description (text before the mermaid block)
      const descBefore = mermaidMatch ? content.substring(0, content.indexOf('```mermaid')).trim() : content;
      return {
        id: section.id || `pf-${index}`,
        title: section.title || `Process Flow ${index + 1}`,
        code: code,
        description: descBefore,
        descriptionBefore: descBefore,
        descriptionAfter: '',
        keySteps: [],
        type: 'process-flows',
        content: content,
        rawContent: section.rawContent || content,
      };
    }).filter(d => d.code); // Only include sections that have mermaid code
  }, []);

  // Sync diagrams: prefer backend sections, fall back to version hook
  useEffect(() => {
    if (isLocalUpdateRef.current) return;

    // PRIMARY: Use backend parsed sections if available
    if (backendSections.length > 0 && !isSectionsLoading) {
      const diagrams = sectionsToDiagrams(backendSections);
      if (diagrams.length > 0) {
        setParsedDiagrams(diagrams);
        diagramContentRef.current = backendContentRef.current;
        return;
      }
    }

    // FALLBACK: Use version hook diagrams
    if (currentVersion) {
      if (currentVersion.diagrams) {
        currentVersion.diagrams.map(key => { key['type'] = 'process-flows'; });
        setParsedDiagrams(currentVersion.diagrams || []);
      } else {
        setParsedDiagrams([]);
      }
      diagramContentRef.current = currentVersion.content;
    }
  }, [backendSections, isSectionsLoading, currentVersion, currentVersionIndex, sectionsToDiagrams]);

  // Use validation hook with version-specific IDs
  const initialValidationState = parsedDiagrams.reduce((acc, diagram) => {
    const versionSpecificId = `${diagram.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  const {
    validatedItems: validatedDiagrams,
    handleValidateItem: handleValidateDiagramBase,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'process-flows',
    initialState: initialValidationState
  });

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'process-flows',
  });

  // Wrapper for version-specific validation
  const handleValidateDiagram = async (diagramId) => {
    const versionSpecificId = `${diagramId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateDiagramBase(versionSpecificId);
  };

  // Use content actions hook
  const {
    isExporting,
    handleExport: handleExportDiagrams,
    getExportContent,
    handleCopy: handleCopyDiagrams,
    handleRefresh: handleRefreshDiagrams
  } = useContentActions(diagramContentRef, 'Process Flows', projectId, conversation?._id);

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainDiagram,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use improve hook
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveDiagram,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Use diagram actions hook - pass onConversationUpdate for LHS sync
  const {
    editingDiagram,
    isEditDialogOpen,
    handleEditDiagram,
    handleCloseEditDialog,
    handleSaveDiagramEdit,
    handleUpdateImprovedContent
  } = useDiagramActions(
    conversation,
    parsedDiagrams,
    setParsedDiagrams,
    diagramContentRef,
    currentVersion,
    updateCurrentVersion,
    isLocalUpdateRef,
    onConversationUpdate,
    showNotification
  );

  // Determine if we should show the split view
  const isProcessFlowsConversation = conversation && (conversation?.taskId || conversation?.task_id) === 'process-flows';
  const showDiagramPanel = parsedDiagrams.length > 0;

  // Handle per-diagram zoom - only updates CSS zoom, no SVG re-render needed
  const handleZoomIn = (diagramId) => {
    setZoomLevels(prev => ({
      ...prev,
      [diagramId]: Math.min((prev[diagramId] || DEFAULT_ZOOM) + 0.2, 3.0)
    }));
  };

  const handleZoomOut = (diagramId) => {
    setZoomLevels(prev => ({
      ...prev,
      [diagramId]: Math.max((prev[diagramId] || DEFAULT_ZOOM) - 0.2, 0.3)
    }));
  };

  // Handle toggling diagram height expansion
  const handleToggleHeight = (diagramId) => {
    setExpandedHeights(prev => ({
      ...prev,
      [diagramId]: !prev[diagramId]
    }));
  };

  // Handle opening diagram in popup
  const handleOpenPopup = useCallback((diagram) => {
    setPopupDiagram(diagram);
    setPopupZoomLevel(1.0);
  }, []);

  const handleClosePopup = useCallback(() => {
    setPopupDiagram(null);
  }, []);

  // Render mermaid diagram inside popup after it opens
  useEffect(() => {
    if (popupDiagram) {
      const timeoutId = setTimeout(() => {
        const popupEl = document.getElementById(`popup-diagram-${popupDiagram.id}`);
        if (popupEl && popupEl.getAttribute('data-processed') === 'false') {
          const code = popupEl.getAttribute('data-code');
          if (code) {
            const uniqueId = `popup-${popupDiagram.id}-${Date.now()}`;
            mermaid.render(uniqueId, fixMermaidSyntax(code)).then(result => {
              popupEl.innerHTML = result.svg;
              popupEl.setAttribute('data-processed', 'true');
              // Clean up temp elements
              document.querySelectorAll(`[id^="d${uniqueId}"]`).forEach(el => el.remove());
            }).catch(err => {
              popupEl.innerHTML = `<p style="color:red;">Diagram render error: ${err.message}</p>`;
            });
          }
        }
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [popupDiagram, popupZoomLevel]);

  // Render diagrams when content or expansion changes (NOT on zoom)
  useEffect(() => {
    let timeoutId;
    if (parsedDiagrams.length > 0) {
      resetDiagramProcessing();
      // Wait 300ms for MUI Accordion Collapse animation to complete (~300ms)
      timeoutId = setTimeout(() => {
        renderMermaidDiagrams(DEFAULT_ZOOM);
      }, 300);
    }
    // Cleanup: cancel stale timeout if effect re-runs before it fires
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [parsedDiagrams, expandedDiagrams]);

  // Hard refresh page after streaming completes for Process Flows
  // This ensures we get the correct content (not broken SSE chunks)
  useEffect(() => {
    const wasStreaming = prevIsStreamingRef.current;
    const streamingJustEnded = wasStreaming && !isStreaming;

    // Update the ref for next render
    prevIsStreamingRef.current = isStreaming;

    if (streamingJustEnded && conversation?._id) {
      console.log('🔄 SplitViewProcessFlows: Streaming ended, performing hard page refresh');

      // Small delay to ensure the message is saved to server before refresh
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }, [isStreaming, conversation?._id]);

  // Auto-scroll panel when streaming
  useEffect(() => {
    if (isStreaming && diagramPanelRef.current && isProcessFlowsConversation) {
      setTimeout(() => {
        if (diagramPanelRef.current) {
          diagramPanelRef.current.scrollTop = diagramPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isProcessFlowsConversation]);

  // Handler for toggling diagram expansion
  const handleDiagramToggle = (diagramId) => {
    const isExpanding = !expandedDiagrams[diagramId];
    setExpandedDiagrams(prev => ({
      ...prev,
      [diagramId]: !prev[diagramId]
    }));

    // Backup render: if expanding, trigger render after accordion animation completes.
    // The useEffect also schedules a render at 300ms; this 500ms fallback catches
    // cases where the first attempt fires before the DOM is fully laid out.
    if (isExpanding) {
      setTimeout(() => {
        renderMermaidDiagrams(DEFAULT_ZOOM);
      }, 500);
    }
  };

  // Helper to render mermaid code to a PNG base64 data URI
  const renderMermaidToPng = async (code, diagramId) => {
    try {
      const uniqueId = `copy-svg-${diagramId}-${Date.now()}`;
      const result = await mermaid.render(uniqueId, fixMermaidSyntax(code));
      // Clean up any temp elements mermaid creates
      document.querySelectorAll(`[id^="d${uniqueId}"]`).forEach(el => el.remove());

      const svgString = result?.svg;
      if (!svgString) return '';

      // Convert SVG to PNG via canvas using base64 data URI to avoid tainted canvas
      return new Promise((resolve) => {
        const img = new Image();
        const base64Svg = btoa(unescape(encodeURIComponent(svgString)));
        const dataUri = `data:image/svg+xml;base64,${base64Svg}`;

        img.onload = () => {
          const scale = 2; // 2x for sharp rendering
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth * scale;
          canvas.height = img.naturalHeight * scale;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };

        img.onerror = () => {
          console.warn('Failed to convert SVG to PNG for copy');
          resolve('');
        };

        img.src = dataUri;
      });
    } catch (err) {
      console.warn('Failed to render mermaid for copy:', err.message);
      return '';
    }
  };

  // Helper to build HTML and plain text for a single diagram
  const buildDiagramCopyContent = (diagram, pngDataUri) => {
    let html = `<h2>${diagram.title}</h2>`;
    let plainText = diagram.title;

    if (diagram.descriptionBefore) {
      html += `<p>${diagram.descriptionBefore}</p>`;
      plainText += `\n\n${diagram.descriptionBefore}`;
    }

    if (pngDataUri) {
      html += `<p><img src="${pngDataUri}" alt="${diagram.title}" /></p>`;
      plainText += `\n\n[Process Flow Diagram]`;
    }

    if (diagram.descriptionAfter) {
      html += `<p>${diagram.descriptionAfter}</p>`;
      plainText += `\n\n${diagram.descriptionAfter}`;
    }

    if (diagram.keySteps && diagram.keySteps.length > 0) {
      html += `<p><strong>Key Steps:</strong></p><ul>${diagram.keySteps.map(step => `<li>${step}</li>`).join('')}</ul>`;
      plainText += `\n\nKey Steps:\n${diagram.keySteps.map(step => `- ${step}`).join('\n')}`;
    }

    return { html, plainText };
  };

  // Handler for copying diagram
  const handleCopyDiagram = async (diagram) => {
    try {
      const pngDataUri = await renderMermaidToPng(diagram.code, diagram.id);
      const { html, plainText } = buildDiagramCopyContent(diagram, pngDataUri);

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' })
        })
      ]);

      showNotification({
        message: "Diagram copied to clipboard",
        type: "success"
      });
    } catch (error) {
      console.error("Error copying diagram:", error);
      showNotification({
        message: "Failed to copy diagram to clipboard",
        type: "error"
      });
    }
  };

  // Handler for copying all diagrams (top bar copy)
  const handleCopyAllDiagrams = async () => {
    try {
      if (!parsedDiagrams || parsedDiagrams.length === 0) {
        showNotification({
          message: "No process flows content to copy",
          type: "warning"
        });
        return;
      }

      let allHtml = '';
      let allPlainText = '';

      for (const diagram of parsedDiagrams) {
        const pngDataUri = await renderMermaidToPng(diagram.code, diagram.id);
        const { html, plainText } = buildDiagramCopyContent(diagram, pngDataUri);

        if (allHtml) {
          allHtml += '<hr/>';
          allPlainText += '\n\n---\n\n';
        }
        allHtml += html;
        allPlainText += plainText;
      }

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([allHtml], { type: 'text/html' }),
          'text/plain': new Blob([allPlainText], { type: 'text/plain' })
        })
      ]);

      showNotification({
        message: "Process flows copied to clipboard",
        type: "success"
      });
    } catch (error) {
      console.error("Error copying process flows:", error);
      showNotification({
        message: "Failed to copy process flows to clipboard",
        type: "error"
      });
    }
  };

  // Handler for deleting diagram
  const handleDeleteDiagram = (diagram) => {
    setDeleteConfirmation({ open: true, diagram });
  };

  // Handler for confirmed delete
  const handleDeleteConfirmed = async () => {
    const diagram = deleteConfirmation.diagram;
    setDeleteConfirmation({ open: false, diagram: null });

    if (!diagram) return;

    try {
      const updatedDiagrams = parsedDiagrams.filter(d => d.id !== diagram.id);

      // Set the flag to prevent reset from currentVersion
      isLocalUpdateRef.current = true;
      setParsedDiagrams(updatedDiagrams);

      // Remove expanded state
      setExpandedDiagrams(prev => {
        const updated = { ...prev };
        delete updated[diagram.id];
        return updated;
      });

      // Reconstruct content
      let updatedContent = '';
      updatedDiagrams.forEach(d => {
        updatedContent += `## ${d.title}\n\n`;
        if (d.descriptionBefore) updatedContent += `${d.descriptionBefore}\n\n`;
        updatedContent += `\`\`\`mermaid\n${d.code}\n\`\`\`\n\n`;
        if (d.descriptionAfter) updatedContent += `${d.descriptionAfter}\n\n`;
        if (d.keySteps && d.keySteps.length > 0) {
          updatedContent += `**Key Steps:**\n`;
          d.keySteps.forEach(step => {
            updatedContent += `- ${step}\n`;
          });
          updatedContent += '\n';
        }
      });

      diagramContentRef.current = updatedContent.trim();

      // Update current version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          diagrams: updatedDiagrams
        });
      }

      // Save to backend - use messageIndex for reliable message matching
      const updatedMessages = [...conversation.messages];
      let messageUpdated = false;

      // First try: Use messageIndex from currentVersion (most reliable)
      if (currentVersion && currentVersion.messageIndex !== undefined) {
        const idx = currentVersion.messageIndex;
        if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
          updatedMessages[idx] = {
            ...updatedMessages[idx],
            content: updatedContent.trim()
          };
          messageUpdated = true;
          console.log('✅ SplitViewProcessFlows delete: Updated message at messageIndex:', idx);
        }
      }

      // Fallback: Find and update the last assistant message
      if (!messageUpdated) {
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            console.log('⚠️ SplitViewProcessFlows delete: Used fallback - updated last assistant message at index:', i);
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Sync to LHS immediately
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ SplitViewProcessFlows: Synced delete to LHS');
      }

      showNotification({
        message: `"${diagram.title}" deleted successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting diagram:', error);
      showNotification({
        message: `Failed to delete "${diagram.title}"`,
        type: 'error'
      });
    }
  };

  // Handler for improving diagram with updated content
  const handleUpdateImprovedDiagram = async () => {
    if (!improveDiagram || !improvedContent) return;

    const success = await handleUpdateImprovedContent(improveDiagram, improvedContent);
    if (success) {
      
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
    }
  };

  // Handler for adding new diagram
  const handleAddNewDiagram = async (mermaidCode, diagramTitle, diagramDescription) => {
    try {
      if (!diagramTitle.trim()) {
        showNotification({
          message: 'Please enter a diagram title',
          type: 'warning'
        });
        return;
      }

      if (!mermaidCode.trim()) {
        showNotification({
          message: 'Please enter mermaid code',
          type: 'warning'
        });
        return;
      }

      // Only add to the latest version
      if (!isLatestVersion) {
        showNotification({
          message: 'Please switch to the latest version to add new diagrams',
          type: 'warning'
        });
        setShowAddDiagramModal(false);
        return;
      }

      // Generate stable ID based on title
      const stableId = diagramTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-');

      const newDiagram = {
        id: stableId,
        title: diagramTitle.trim(),
        code: mermaidCode.trim(),
        descriptionBefore: diagramDescription?.trim() || '',
        descriptionAfter: '',
        keySteps: []
      };

      // Add the new diagram to the current diagrams
      const updatedDiagrams = [...parsedDiagrams, newDiagram];

      // Set the flag to prevent reset from currentVersion
      isLocalUpdateRef.current = true;
      setParsedDiagrams(updatedDiagrams);

      // Expand the new diagram by default
      setExpandedDiagrams(prev => ({ ...prev, [newDiagram.id]: true }));

      // Reconstruct content with new diagram
      let updatedContent = '';
      updatedDiagrams.forEach(d => {
        updatedContent += `## ${d.title}\n\n`;
        if (d.descriptionBefore) updatedContent += `${d.descriptionBefore}\n\n`;
        updatedContent += `\`\`\`mermaid\n${d.code}\n\`\`\`\n\n`;
        if (d.descriptionAfter) updatedContent += `${d.descriptionAfter}\n\n`;
        if (d.keySteps && d.keySteps.length > 0) {
          updatedContent += `**Key Steps:**\n`;
          d.keySteps.forEach(step => {
            updatedContent += `- ${step}\n`;
          });
          updatedContent += '\n';
        }
      });

      // Update content ref
      diagramContentRef.current = updatedContent.trim();

      // Update current version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          diagrams: updatedDiagrams
        });
      }

      // Save to backend - use messageIndex for reliable message matching
      const updatedMessages = [...conversation.messages];
      let messageUpdated = false;

      // First try: Use messageIndex from currentVersion (most reliable)
      if (currentVersion && currentVersion.messageIndex !== undefined) {
        const idx = currentVersion.messageIndex;
        if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
          updatedMessages[idx] = {
            ...updatedMessages[idx],
            content: updatedContent.trim()
          };
          messageUpdated = true;
          console.log('✅ SplitViewProcessFlows add: Updated message at messageIndex:', idx);
        }
      }

      // Fallback: Find and update the last assistant message
      if (!messageUpdated) {
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            console.log('⚠️ SplitViewProcessFlows add: Used fallback - updated last assistant message at index:', i);
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Sync to LHS immediately
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ SplitViewProcessFlows: Synced add to LHS');
      }

      showNotification({
        message: 'Process flow diagram added successfully',
        type: 'success'
      });

      // Close modal
      setShowAddDiagramModal(false);

      // Scroll to the new diagram
      setTimeout(() => {
        if (diagramPanelRef.current) {
          diagramPanelRef.current.scrollTop = diagramPanelRef.current.scrollHeight;
        }
      }, 100);

      // Re-render diagrams
      resetDiagramProcessing();
      setTimeout(() => {
        renderMermaidDiagrams(DEFAULT_ZOOM);
      }, 100);
    } catch (error) {
      console.error('Error adding diagram:', error);
      showNotification({
        message: 'Failed to add process flow diagram',
        type: 'error'
      });
    }
  };

  // Don't show split view if not a process flows conversation
  if (!isProcessFlowsConversation || (!showDiagramPanel && !isStreaming)) {
    return <>{children}</>;
  }

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      mt: 5,
    }}>
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(false),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Process flows panel */}
        <Box
          ref={diagramPanelRef}
          sx={{
            ...getRightPanelStyles(false),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <SectionHeader label='Process Flows' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Version selector */}
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={(idx) => {
                      // Reset local update flag when switching versions
                      isLocalUpdateRef.current = false;
                      setCurrentVersionIndex(idx);
                    }}
                    disabled={isStreaming}
                  />
                )}

                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  onAdd={isLatestVersion ? () => setShowAddDiagramModal(true) : null}
                  getExportContent={getExportContent}
                  onExport={handleExportDiagrams}
                  isExporting={isExporting}
                  onCopy={handleCopyAllDiagrams}
                  onRefresh={handleRefreshDiagrams}
                  disabled={!diagramContentRef.current}
                  addTooltip={!isLatestVersion ? "Switch to latest version to add diagrams" : "Add new process flow diagram"}
                  exportTooltip="Export process flows as markdown"
                  copyTooltip="Copy process flows to clipboard"
                  refreshTooltip="Refresh diagrams"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'process-flows'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedDiagrams}
              totalItems={backendSections?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            {/* Content section */}
            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
            }}>
              {isStreaming ? (
                <LoadingBox message='Generating process flows...' spinnerSize={20} />
              ) : showDiagramPanel && parsedDiagrams.length > 0 ? (
                <Box>
                  {parsedDiagrams.map((diagram, index) => {
                    const versionSpecificId = `${diagram.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                    const isValidated = validatedDiagrams[versionSpecificId] || false;

                    return (
                      <ProcessFlowDiagramItem
                        key={diagram.id}
                        diagram={diagram}
                        index={index}
                        isExpanded={expandedDiagrams[diagram.id]}
                        isHeightExpanded={expandedHeights[diagram.id] || false}
                        isValidated={isValidated}
                        memberType={getValidatorType(versionSpecificId)}
                        isAssignedValidator={isAssignedValidator}
                        zoomLevel={zoomLevels[diagram.id] || DEFAULT_ZOOM}
                        showImproveInput={showImproveInput}
                        improvePrompt={improvePrompt}
                        currentVersion={currentVersion}
                        currentVersionIndex={currentVersionIndex}
                        onToggle={handleDiagramToggle}
                        onToggleHeight={handleToggleHeight}
                        onOpenPopup={handleOpenPopup}
                        onValidate={handleValidateDiagram}
                        // onEdit={() => handleEditDiagram(diagram)}
                        onImprove={() => handleImproveClick(diagram)}
                        onExplain={() => requestExplanation(diagram)}
                        onCopy={() => handleCopyDiagram(diagram)}
                        onDelete={() => handleDeleteDiagram(diagram)}
                        onZoomIn={() => handleZoomIn(diagram.id)}
                        onZoomOut={() => handleZoomOut(diagram.id)}
                        onImprovePromptChange={setImprovePrompt}
                        onSendImprove={() => handleSendImprove(diagram)}
                        onCancelImprove={handleCancelImprove}
                        registerSectionRef={registerSectionRef}
                        showNotification={showNotification}
                        onComment={() => openCommentPanel(diagram.id)}
                        commentCount={commentCounts[diagram.id]?.open || 0}
                      />
                    );
                  })}
                </Box>
              ) : (
                <EmptyStateBox message="No process flows found in this conversation yet." />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, diagram: null })}
        title="Delete diagram"
        message={`Are you sure you want to delete "${deleteConfirmation.diagram?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height="auto"
        onConfirm={handleDeleteConfirmed}
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainDiagram?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedDiagram}
        section={improveDiagram}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedDiagrams || []).find(d => d.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />

      {/* Mermaid Visual Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '75vw',
            height: '90vh',
            maxWidth: 'none',
            maxHeight: 'none',
            m: 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Edit Diagram
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, height: '100%' }}>
          <MermaidEditor
            initialCode={editingDiagram?.code || ''}
            initialTitle={editingDiagram?.title || ''}
            height="100%"
            onSave={handleSaveDiagramEdit}
            onCancel={handleCloseEditDialog}
            showTitleField={true}
          />
        </DialogContent>
      </Dialog>

      {/* Add New Diagram Modal */}
      <AddDiagramModal
        open={showAddDiagramModal}
        onClose={() => setShowAddDiagramModal(false)}
        onSave={handleAddNewDiagram}
        title="Add New Process Flow Diagram"
        titleLabel="Diagram Title"
        titlePlaceholder="Enter diagram title (e.g., User Authentication Flow)"
        descriptionLabel="Description"
        descriptionPlaceholder="Enter a brief description of this process flow diagram"
      />

      {/* Diagram Popup Dialog */}
      <Dialog
        open={!!popupDiagram}
        onClose={handleClosePopup}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '90vw',
            height: '90vh',
            maxWidth: 'none',
            maxHeight: 'none',
            m: 1,
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: '1px solid #e0e0e0',
        }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            {popupDiagram?.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              disabled={popupZoomLevel <= 0.3}
              onClick={() => setPopupZoomLevel(prev => Math.max(prev - 0.2, 0.3))}
              sx={{
                border: '1px solid #e0e0e0',
                width: 32,
                height: 32,
              }}
            >
              <ZoomOutIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center', color: 'text.secondary', fontSize: '0.8rem' }}>
              {Math.round(popupZoomLevel * 100)}%
            </Typography>
            <IconButton
              size="small"
              disabled={popupZoomLevel >= 3.0}
              onClick={() => setPopupZoomLevel(prev => Math.min(prev + 0.2, 3.0))}
              sx={{
                border: '1px solid #e0e0e0',
                width: 32,
                height: 32,
              }}
            >
              <ZoomInIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleClosePopup}
              sx={{
                ml: 1,
                border: '1px solid #e0e0e0',
                width: 32,
                height: 32,
              }}
            >
              <CloseIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          p: 3,
          overflow: 'auto',
          backgroundColor: '#f9f9f9',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}>
          {popupDiagram && (
            <Box sx={{
              zoom: popupZoomLevel,
              minWidth: '100%',
              width: 'max-content',
              height: 'auto',
              padding: 2,
              '& svg': {
                minWidth: '600px',
                height: 'auto',
                display: 'block',
                '& text': {
                  fontFamily: '"Arial", sans-serif',
                  fontSize: '14px !important',
                  textRendering: 'optimizeLegibility',
                  whiteSpace: 'pre',
                }
              }
            }}>
              <div
                id={`popup-diagram-${popupDiagram.id}`}
                className="mermaid-diagram"
                data-code={popupDiagram.code}
                data-processed="false"
                style={{ minHeight: '200px' }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, gap: 1.5, minHeight: '200px' }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading diagram...
                  </Typography>
                </Box>
              </div>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SplitViewProcessFlows;
